import { useQuery } from "@tanstack/react-query";
import type { ClientVersionResponse } from "@/types/ClientVersion";

const POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

async function fetchClientVersion(): Promise<ClientVersionResponse> {
  // Add cache-busting timestamp to prevent browser/CDN caching
  const cacheBuster = Date.now();
  const response = await fetch(`/version.json?_=${cacheBuster}`, {
    method: "GET",
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch version: ${response.statusText}`);
  }

  return response.json();
}

export function useClientVersionQuery() {
  return useQuery({
    queryKey: ["clientVersion"],
    queryFn: fetchClientVersion,
    staleTime: POLLING_INTERVAL,
    refetchInterval: POLLING_INTERVAL,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    refetchOnWindowFocus: true, // Check on tab focus
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to check if a new version is available
 */
export function useVersionCheck() {
  const { data, isError, isLoading } = useClientVersionQuery();

  const currentVersion = import.meta.env.VITE_COMMIT_HASH;
  const serverVersion = data?.version;

  const isNewVersionAvailable = Boolean(
    serverVersion && currentVersion && serverVersion !== currentVersion && currentVersion !== "unknown",
  );

  return {
    isNewVersionAvailable,
    currentVersion,
    serverVersion,
    isError,
    isLoading,
  };
}
